"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Check as CheckCircle2, Clock, Droplets, Flame, PhoneCall, RotateCcw, Volume2, Waves } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const warningSigns = [{
  id: 1,
  title: "Slow Water Drainage",
  indicator: "Water pools around your feet or sits in sinks for minutes.",
  cause: "Accumulated hair, grease, soap scum, or mineral build-up narrowing pipe diameter.",
  urgency: "Moderate Risk",
  iconName: "droplets"
}, {
  id: 2,
  title: "Gurgling or Bubbling Sounds",
  indicator: "Odd bubbling noises coming from toilets or sinks after running water.",
  cause: "Trapped air pockets forced through localized obstructions within the line.",
  urgency: "High Risk",
  iconName: "volume"
}, {
  id: 3,
  title: "Unpleasant Sewer Odors",
  indicator: "Rotten egg or raw sewage smells wafting from sink and shower drains.",
  cause: "Decomposing matter, biofilm build-up, or a broken drain trap seal.",
  urgency: "Immediate Action",
  iconName: "alert"
}, {
  id: 4,
  title: "Frequent Recurring Clogs",
  indicator: "Drains clear temporarily with a plunger but stop up again days later.",
  cause: "Deep clogs, solidified grease coats, or invasive tree root intrusion in main line.",
  urgency: "High Risk",
  iconName: "rotate"
}, {
  id: 5,
  title: "Multiple Fixtures Backing Up",
  indicator: "Flushing the toilet causes water to back up into the shower or tub.",
  cause: "Severe obstruction or blockage in the main sewer lateral line.",
  urgency: "Emergency",
  iconName: "waves"
}, {
  id: 6,
  title: "Drain Flies or Gnats",
  indicator: "Persistent small insects hovering around bathroom or kitchen sinks.",
  cause: "Organic sludge lining the internal pipe walls where drain flies breed.",
  urgency: "Moderate Risk",
  iconName: "flame"
}];
const preventionChecklist = ["Camera video inspection to identify root causes without destructive digging", "Professional motorized snaking and high-pressure hydro jetting options", "Safe removal of heavy grease, hardened soap scale, and invasive tree roots", "Complete flow restoration verified with post-cleaning testing"];
export default function DrainSigns() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "droplets":
        return <Droplets className="size-6 text-primary" />;
      case "volume":
        return <Volume2 className="size-6 text-primary" />;
      case "alert":
        return <AlertTriangle className="size-6 text-accent-foreground" />;
      case "rotate":
        return <RotateCcw className="size-6 text-primary" />;
      case "waves":
        return <Waves className="size-6 text-destructive" />;
      case "flame":
        return <Flame className="size-6 text-primary" />;
      default:
        return <Droplets className="size-6 text-primary" />;
    }
  };
  const getUrgencyBadge = (urgency: string) => {
    if (urgency === "Emergency") {
      return <Badge variant="destructive" className="font-semibold">
          {urgency}
        </Badge>;
    }
    if (urgency === "Immediate Action") {
      return <Badge className="bg-accent text-accent-foreground font-semibold">
          {urgency}
        </Badge>;
    }
    return <Badge variant="secondary" className="font-medium">
        {urgency}
      </Badge>;
  };
  return <section id="drainwarningsigns" data-nav="light" className="relative bg-background py-16 sm:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 gap-1.5 px-3 py-1 text-xs">
            <AlertTriangle className="size-3.5 text-accent-foreground" />
            Early Warning System
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Warning Signs Your Drain Needs Professional Cleaning
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Minor drain hiccups often signal deeper pipeline obstructions. Catching these early symptoms prevents catastrophic sewer backups, pipe burst damage, and expensive repairs in your Jonesboro home.
          </p>
        </div>

        {/* Warning Signs Grid */}
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {warningSigns.map((item, index) => <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.35,
          delay: index * 0.08
        }} className="h-full">
              <Card className="flex h-full flex-col justify-between border-border bg-card shadow-sm transition-all hover:border-primary/40 hover:shadow-md">
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-secondary">
                      {getIcon(item.iconName)}
                    </div>
                    {getUrgencyBadge(item.urgency)}
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="mt-2 text-sm text-foreground/80 font-medium leading-relaxed">
                      {item.indicator}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="rounded-lg bg-muted/60 p-3.5 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">Underlying cause: </span>
                    {item.cause}
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Solution & Call to Action Banner */}
        <div className="mt-14 rounded-2xl border border-border bg-secondary/50 p-6 sm:p-10 lg:p-12">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-md bg-background px-3 py-1 text-xs font-semibold text-primary shadow-xs">
                <Clock className="size-3.5" />
                24/7 Rapid Response in Jonesboro, GA
              </div>
              <h3 className="mt-3 font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Do Not Wait for a Sewage Overflow
              </h3>
              <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                Chemical store-bought cleaners only melt superficial holes through grease while corroding metal pipes. Our licensed plumbers clear the entire pipe diameter safely and completely.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {preventionChecklist.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                    <span>{benefit}</span>
                  </div>)}
              </div>
            </div>

            <div className="flex flex-col gap-3.5 sm:flex-row lg:col-span-5 lg:flex-col lg:items-end">
              <Button asChild size="lg" className="w-full justify-center text-base font-semibold shadow-md sm:w-auto lg:w-full">
                <Link to="/contact/">
                  <PhoneCall className="size-4" />
                  Schedule Drain Cleaning
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full justify-center text-base sm:w-auto lg:w-full">
                <Link to="/problems/">
                  <span>Explore Common Problems</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}